import React, { useState, useEffect, useRef } from 'react';
import { convertSecto12Hour } from "../../libs.jsx";

const DayTimeRangeSlider = (props) => {
    const { start = 0, end = 86400, setStart, setEnd } = props;

    const [isDragging, setIsDragging] = useState(null); // 'start' or 'end' or null
    const sliderRef = useRef(null);

    const maxTime = 86400; // 24 hours in seconds

    // Add global mouse event listeners for proper drag handling
    useEffect(() => {
        const handleGlobalMouseUp = () => {
            if (isDragging) {
                setIsDragging(null);
            }
        };

        const handleGlobalMouseMove = (e) => {
            if (isDragging && sliderRef.current) {
                updateHandlePosition(e, isDragging);
            }
        };

        // Only add global listeners when dragging
        if (isDragging) {
            document.addEventListener('mouseup', handleGlobalMouseUp);
            document.addEventListener('mousemove', handleGlobalMouseMove);
        }

        // Cleanup function
        return () => {
            document.removeEventListener('mouseup', handleGlobalMouseUp);
            document.removeEventListener('mousemove', handleGlobalMouseMove);
        };
    }, [isDragging]);

    const handleMouseDown = (e, handle) => {
        setIsDragging(handle);

        // Immediately update the position on mouse down
        updateHandlePosition(e, handle);

        // Prevent default and stop propagation
        e.preventDefault();
        e.stopPropagation();
    };

    const updateHandlePosition = (e, handle) => {
        if (!sliderRef.current) return;

        const rect = sliderRef.current.getBoundingClientRect();
        const x = e.clientX - rect.left;
        const percentage = Math.max(0, Math.min(1, x / rect.width));
        const rawTime = Math.round(percentage * maxTime);

        // Snap to 5-minute increments (300 seconds)
        const fiveMinuteIncrement = 300; // 5 minutes in seconds
        const newTime = Math.round(rawTime / fiveMinuteIncrement) * fiveMinuteIncrement;

        if (handle === 'start' && setStart) {
            setStart(newTime);
        } else if (handle === 'end' && setEnd) {
            setEnd(newTime);
        }
    };

    const getSliderTrackStyle = () => {
        const startPercent = (start / maxTime) * 100;
        const endPercent = (end / maxTime) * 100;

        // Handle midnight crossing
        if (end < start) {
            return {
                background: `linear-gradient(to right, 
                    #007bff 0%, #007bff ${endPercent}%, 
                    #e9ecef ${endPercent}%, #e9ecef ${startPercent}%, 
                    #007bff ${startPercent}%, #007bff 100%)`
            };
        } else {
            return {
                background: `linear-gradient(to right, 
                    #e9ecef 0%, #e9ecef ${startPercent}%, 
                    #007bff ${startPercent}%, #007bff ${endPercent}%, 
                    #e9ecef ${endPercent}%, #e9ecef 100%)`
            };
        }
    };

    const getHandleStyle = (time) => {
        const percent = (time / maxTime) * 100;
        return {
            left: `${percent}%`,
            transform: 'translateX(-50%)'
        };
    };

    const formatTimeDisplay = (time) => {
        if (time === 0) return '12:00 AM';
        if (time === maxTime) return '11:59 PM';
        return convertSecto12Hour(time);
    };

    const getTimeRangeDescription = () => {
        if (start === 0 && end === maxTime) {
            return 'All Day';
        } else if (end < start) {
            return `${formatTimeDisplay(start)} - ${formatTimeDisplay(end)} (Crosses Midnight)`;
        } else {
            return `${formatTimeDisplay(start)} - ${formatTimeDisplay(end)}`;
        }
    };

    return (
            <div className={"bg-white px-2 py-2 mt-3"}>
                <div className="mb-3">
                    <label className={"form-label"}>Time Range: {getTimeRangeDescription()}</label>
                </div>
                <div className={"mb-3 px-2"}>
                    <div ref={sliderRef} className={"position-relative"}
                         style={{height: '10px', background: '#e9ecef', borderRadius: '5px', marginTop: '20px',
                                marginBottom: '30px', ...getSliderTrackStyle()}}>
                        <div className={"position-absolute"}
                             style={{...getHandleStyle(start), top: '25px', transform: 'translateX(-50%)',
                                     backgroundColor: '#333', color: '#fff', padding: '4px 8px', borderRadius: '4px',
                                     fontSize: '12px', whiteSpace: 'nowrap', zIndex: 1, pointerEvents: 'none'}}>
                            {formatTimeDisplay(start)}
                        </div>

                        {/* End Handle Tooltip */}
                        <div className="position-absolute"
                             style={{...getHandleStyle(end), top: '25px', transform: 'translateX(-50%)',
                                     backgroundColor: '#333', color: '#fff', padding: '4px 8px', borderRadius: '4px',
                                     fontSize: '12px', whiteSpace: 'nowrap', zIndex: 1, pointerEvents: 'none'}}>
                            {formatTimeDisplay(end)}
                        </div>

                        {/* Start Handle */}
                        <div className="position-absolute"
                             style={{
                                ...getHandleStyle(start),
                                width: '20px',
                                height: '20px',
                                backgroundColor: '#007bff',
                                border: '2px solid #fff',
                                borderRadius: '50%',
                                cursor: 'grab',
                                top: '-5px',
                                boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
                                zIndex: 10,
                                pointerEvents: 'auto'
                            }}
                            onMouseDown={(e) => handleMouseDown(e, 'start')}
                        />

                        {/* End Handle */}
                        <div className="position-absolute"
                            style={{
                                ...getHandleStyle(end),
                                width: '20px',
                                height: '20px',
                                backgroundColor: '#007bff',
                                border: '2px solid #fff',
                                borderRadius: '50%',
                                cursor: 'grab',
                                top: '-5px',
                                boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
                                zIndex: 10,
                                pointerEvents: 'auto'
                            }}
                            onMouseDown={(e) => handleMouseDown(e, 'end')}
                        />
                    </div>

                    <div className="d-flex justify-content-between mt-2">
                        <small className="text-muted">12:00 AM</small>
                        <small className="text-muted">6:00 AM</small>
                        <small className="text-muted">12:00 PM</small>
                        <small className="text-muted">6:00 PM</small>
                        <small className="text-muted">11:59 PM</small>
                    </div>
                </div>
            </div>
    );
};

export default DayTimeRangeSlider;